import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Play, Pause, Copy, Trash2, Settings, MessageSquare, HelpCircle,
  GitBranch, Clock, Bot, UserCheck, ClipboardList, Globe, Zap, ArrowRight,
  GripVertical, X, ChevronDown, ChevronRight, Eye, Save
} from 'lucide-react';
import {
  chatflowsApi,
  Chatflow,
  ChatflowDefinition,
  ChatflowNode,
  ChatflowEdge,
  ChatflowSession,
  ChatflowTemplate
} from '@/services/campaignsApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ============================================================================
// CONSTANTS
// ============================================================================

type NodeType = ChatflowNode['type'];

interface NodeTypeConfig {
  type: NodeType;
  label: string;
  icon: React.FC<{ className?: string }>;
  color: string;
  bgColor: string;
  borderColor: string;
}

const NODE_TYPE_CONFIGS: NodeTypeConfig[] = [
  { type: 'message', label: 'Message', icon: MessageSquare, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-300' },
  { type: 'question', label: 'Question', icon: HelpCircle, color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-300' },
  { type: 'condition', label: 'Condition', icon: GitBranch, color: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-300' },
  { type: 'delay', label: 'Delay', icon: Clock, color: 'text-gray-600', bgColor: 'bg-gray-50', borderColor: 'border-gray-300' },
  { type: 'ai_response', label: 'AI Response', icon: Bot, color: 'text-purple-600', bgColor: 'bg-purple-50', borderColor: 'border-purple-300' },
  { type: 'human_handoff', label: 'Human Handoff', icon: UserCheck, color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-300' },
  { type: 'survey', label: 'Survey', icon: ClipboardList, color: 'text-teal-600', bgColor: 'bg-teal-50', borderColor: 'border-teal-300' },
  { type: 'api_call', label: 'API Call', icon: Zap, color: 'text-pink-600', bgColor: 'bg-pink-50', borderColor: 'border-pink-300' },
  { type: 'channel_switch', label: 'Channel Switch', icon: Globe, color: 'text-indigo-600', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-300' },
];

function getNodeConfig(type: NodeType): NodeTypeConfig {
  return NODE_TYPE_CONFIGS.find(c => c.type === type) || NODE_TYPE_CONFIGS[0];
}

// ============================================================================
// SORTABLE NODE COMPONENT
// ============================================================================

interface SortableNodeProps {
  node: ChatflowNode;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  config: NodeTypeConfig;
}

function SortableNode({ node, isSelected, onSelect, onDelete, config }: SortableNodeProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: node.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = config.icon;
  const preview = node.data?.content || node.data?.message || node.data?.url || node.data?.system_prompt || `${config.label} node`;

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${config.bgColor} ${
          isSelected ? `${config.borderColor} ring-2 ring-offset-1 ring-blue-400` : 'border-transparent hover:border-gray-300'
        }`}
        onClick={onSelect}
      >
        <div className="flex items-center gap-2">
          <button
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <Icon className={`w-5 h-5 ${config.color}`} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-800">{config.label}</div>
            <div className="text-xs text-gray-500 truncate">{typeof preview === 'string' ? preview : ''}</div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-gray-400 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// NODE CONFIGURATION PANEL
// ============================================================================

interface NodeConfigPanelProps {
  node: ChatflowNode;
  onUpdate: (nodeId: string, data: Record<string, any>) => void;
  onClose: () => void;
}

function NodeConfigPanel({ node, onUpdate, onClose }: NodeConfigPanelProps) {
  const config = getNodeConfig(node.type);
  const Icon = config.icon;
  const [localData, setLocalData] = useState<Record<string, any>>(node.data || {});

  useEffect(() => {
    setLocalData(node.data || {});
  }, [node.id, node.data]);

  const handleChange = (key: string, value: any) => {
    const updated = { ...localData, [key]: value };
    setLocalData(updated);
    onUpdate(node.id, updated);
  };

  const renderFields = () => {
    switch (node.type) {
      case 'message':
        return (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message Content</label>
            <textarea
              className="w-full border rounded-md p-2 text-sm min-h-[100px] focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              value={localData.content || ''}
              onChange={(e) => handleChange('content', e.target.value)}
              placeholder="Enter message content..."
            />
            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Channel</label>
            <select
              className="w-full border rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-400"
              value={localData.channel || 'sms'}
              onChange={(e) => handleChange('channel', e.target.value)}
            >
              <option value="sms">SMS</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </>
        );

      case 'question':
        return (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
            <textarea
              className="w-full border rounded-md p-2 text-sm min-h-[100px] focus:ring-2 focus:ring-green-400 focus:border-green-400"
              value={localData.content || ''}
              onChange={(e) => handleChange('content', e.target.value)}
              placeholder="Enter question text..."
            />
            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Expected Response Format</label>
            <select
              className="w-full border rounded-md p-2 text-sm focus:ring-2 focus:ring-green-400"
              value={localData.response_format || 'free_text'}
              onChange={(e) => handleChange('response_format', e.target.value)}
            >
              <option value="free_text">Free Text</option>
              <option value="yes_no">Yes / No</option>
              <option value="numeric">Numeric</option>
              <option value="multiple_choice">Multiple Choice</option>
            </select>
            {localData.response_format === 'multiple_choice' && (
              <>
                <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Options (one per line)</label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm min-h-[60px]"
                  value={localData.options || ''}
                  onChange={(e) => handleChange('options', e.target.value)}
                  placeholder="Option A&#10;Option B&#10;Option C"
                />
              </>
            )}
          </>
        );

      case 'condition':
        return (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">Condition Rules</label>
            <p className="text-xs text-gray-500 mb-2">Match response patterns to route the conversation.</p>
            {(localData.conditions || [{ pattern: '', target_label: '' }]).map((cond: any, idx: number) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  className="flex-1 border rounded-md p-2 text-sm"
                  placeholder="Pattern (e.g. yes, 1, agree)"
                  value={cond.pattern || ''}
                  onChange={(e) => {
                    const conditions = [...(localData.conditions || [{ pattern: '', target_label: '' }])];
                    conditions[idx] = { ...conditions[idx], pattern: e.target.value };
                    handleChange('conditions', conditions);
                  }}
                />
                <input
                  className="flex-1 border rounded-md p-2 text-sm"
                  placeholder="Branch label"
                  value={cond.target_label || ''}
                  onChange={(e) => {
                    const conditions = [...(localData.conditions || [{ pattern: '', target_label: '' }])];
                    conditions[idx] = { ...conditions[idx], target_label: e.target.value };
                    handleChange('conditions', conditions);
                  }}
                />
                <button
                  onClick={() => {
                    const conditions = (localData.conditions || []).filter((_: any, i: number) => i !== idx);
                    handleChange('conditions', conditions);
                  }}
                  className="text-red-400 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => handleChange('conditions', [...(localData.conditions || []), { pattern: '', target_label: '' }])}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              + Add condition
            </button>
          </>
        );

      case 'delay':
        return (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">Delay Duration</label>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Hours</label>
                <input
                  type="number"
                  min="0"
                  className="w-full border rounded-md p-2 text-sm"
                  value={localData.hours || 0}
                  onChange={(e) => handleChange('hours', parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Minutes</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  className="w-full border rounded-md p-2 text-sm"
                  value={localData.minutes || 0}
                  onChange={(e) => handleChange('minutes', parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
          </>
        );

      case 'ai_response':
        return (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">System Prompt</label>
            <textarea
              className="w-full border rounded-md p-2 text-sm min-h-[120px] focus:ring-2 focus:ring-purple-400 focus:border-purple-400"
              value={localData.system_prompt || ''}
              onChange={(e) => handleChange('system_prompt', e.target.value)}
              placeholder="You are a helpful assistant..."
            />
            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Max Conversation Turns</label>
            <input
              type="number"
              min="1"
              max="50"
              className="w-full border rounded-md p-2 text-sm"
              value={localData.max_turns || 5}
              onChange={(e) => handleChange('max_turns', parseInt(e.target.value) || 5)}
            />
            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Confidence Threshold</label>
            <input
              type="range"
              min="0"
              max="100"
              className="w-full"
              value={(localData.confidence_threshold || 0.7) * 100}
              onChange={(e) => handleChange('confidence_threshold', parseInt(e.target.value) / 100)}
            />
            <div className="text-xs text-gray-500 text-right">{((localData.confidence_threshold || 0.7) * 100).toFixed(0)}%</div>
          </>
        );

      case 'human_handoff':
        return (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Rule</label>
            <select
              className="w-full border rounded-md p-2 text-sm focus:ring-2 focus:ring-orange-400"
              value={localData.assignment_rule || 'round_robin'}
              onChange={(e) => handleChange('assignment_rule', e.target.value)}
            >
              <option value="round_robin">Round Robin</option>
              <option value="least_busy">Least Busy Agent</option>
              <option value="specific_agent">Specific Agent</option>
              <option value="manager">Manager</option>
            </select>
            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Notification Method</label>
            <select
              className="w-full border rounded-md p-2 text-sm"
              value={localData.notification_method || 'in_app'}
              onChange={(e) => handleChange('notification_method', e.target.value)}
            >
              <option value="in_app">In-App Notification</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="slack">Slack</option>
            </select>
          </>
        );

      case 'survey':
        return (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">Survey ID</label>
            <input
              type="text"
              className="w-full border rounded-md p-2 text-sm focus:ring-2 focus:ring-teal-400"
              value={localData.survey_id || ''}
              onChange={(e) => handleChange('survey_id', e.target.value)}
              placeholder="Enter survey ID or select below"
            />
            <p className="text-xs text-gray-500 mt-1">Connect this node to an existing survey to collect structured responses.</p>
          </>
        );

      case 'api_call':
        return (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
            <input
              type="text"
              className="w-full border rounded-md p-2 text-sm focus:ring-2 focus:ring-pink-400"
              value={localData.url || ''}
              onChange={(e) => handleChange('url', e.target.value)}
              placeholder="https://api.example.com/endpoint"
            />
            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Method</label>
            <select
              className="w-full border rounded-md p-2 text-sm"
              value={localData.method || 'GET'}
              onChange={(e) => handleChange('method', e.target.value)}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Headers (JSON)</label>
            <textarea
              className="w-full border rounded-md p-2 text-sm min-h-[60px] font-mono text-xs"
              value={localData.headers || '{}'}
              onChange={(e) => handleChange('headers', e.target.value)}
              placeholder='{"Authorization": "Bearer ..."}'
            />
            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Body (JSON)</label>
            <textarea
              className="w-full border rounded-md p-2 text-sm min-h-[60px] font-mono text-xs"
              value={localData.body || ''}
              onChange={(e) => handleChange('body', e.target.value)}
              placeholder='{"key": "value"}'
            />
          </>
        );

      case 'channel_switch':
        return (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Channel</label>
            <select
              className="w-full border rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-400"
              value={localData.target_channel || 'sms'}
              onChange={(e) => handleChange('target_channel', e.target.value)}
            >
              <option value="sms">SMS</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="voice">Voice</option>
              <option value="webchat">Web Chat</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Switch the conversation to a different communication channel.</p>
          </>
        );

      default:
        return <p className="text-sm text-gray-500">No configuration available for this node type.</p>;
    }
  };

  return (
    <div className="w-80 border-l bg-white overflow-y-auto">
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${config.color}`} />
          <h3 className="font-semibold text-gray-800">{config.label} Settings</h3>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4 space-y-1">
        {renderFields()}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface ChatflowBuilderProps {
  organizationId: number;
}

type View = 'list' | 'editor' | 'sessions';

export const ChatflowBuilder: React.FC<ChatflowBuilderProps> = ({ organizationId }) => {
  // ---------- State ----------
  const [view, setView] = useState<View>('list');
  const [chatflows, setChatflows] = useState<Chatflow[]>([]);
  const [currentChatflow, setCurrentChatflow] = useState<Chatflow | null>(null);
  const [nodes, setNodes] = useState<ChatflowNode[]>([]);
  const [edges, setEdges] = useState<ChatflowEdge[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<ChatflowTemplate[]>([]);
  const [sessions, setSessions] = useState<ChatflowSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [descValue, setDescValue] = useState('');
  const [showTemplates, setShowTemplates] = useState(true);
  const [sessionMessages, setSessionMessages] = useState<Record<string, any[]>>({});
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [humanMessage, setHumanMessage] = useState('');

  // ---------- DnD Sensors ----------
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateTransformations: sortableKeyboardCoordinates })
  );

  // ---------- Data Fetching ----------
  const loadChatflows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await chatflowsApi.listChatflows({ organization_id: organizationId });
      setChatflows(Array.isArray(data) ? data : (data as any).chatflows || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load chatflows');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await chatflowsApi.getTemplates();
      setTemplates(data);
    } catch {
      // templates are optional
    }
  }, []);

  const loadSessions = useCallback(async (chatflowId: string) => {
    try {
      const data = await chatflowsApi.getChatflowSessions(chatflowId);
      setSessions(Array.isArray(data) ? data : (data as any).sessions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load sessions');
    }
  }, []);

  useEffect(() => {
    loadChatflows();
    loadTemplates();
  }, [loadChatflows, loadTemplates]);

  // ---------- Chatflow CRUD ----------
  const createChatflow = async (template?: ChatflowTemplate) => {
    try {
      const newFlow = await chatflowsApi.createChatflow({
        name: template ? `${template.name} (Copy)` : 'New Chatflow',
        description: template?.description || '',
        organization_id: organizationId,
        flow_definition: template?.flow_definition || { nodes: [], edges: [] },
      });
      openEditor(newFlow);
      loadChatflows();
    } catch (err: any) {
      setError(err.message || 'Failed to create chatflow');
    }
  };

  const openEditor = async (chatflow: Chatflow) => {
    try {
      // Fetch full chatflow with flow_definition (list view may not include it)
      const full = await chatflowsApi.getChatflow(chatflow.id);
      const flowDef = full.flow_definition || { nodes: [], edges: [] };
      setCurrentChatflow(full);
      setNodes(flowDef.nodes || []);
      setEdges(flowDef.edges || []);
      setNameValue(full.name);
      setDescValue(full.description || '');
      setSelectedNodeId(null);
      setView('editor');
    } catch (err: any) {
      // Fallback to what we have
      setCurrentChatflow(chatflow);
      setNodes(chatflow.flow_definition?.nodes || []);
      setEdges(chatflow.flow_definition?.edges || []);
      setNameValue(chatflow.name);
      setDescValue(chatflow.description || '');
      setSelectedNodeId(null);
      setView('editor');
    }
  };

  const openSessions = async (chatflow: Chatflow) => {
    setCurrentChatflow(chatflow);
    setSelectedSessionId(null);
    setView('sessions');
    await loadSessions(chatflow.id);
  };

  const saveChatflow = async () => {
    if (!currentChatflow) return;
    setSaving(true);
    try {
      await chatflowsApi.updateChatflow(currentChatflow.id, {
        name: nameValue,
        description: descValue,
        flow_definition: { nodes, edges },
      });
      // Keep current state intact — don't overwrite with the minimal API response
      setCurrentChatflow({
        ...currentChatflow,
        name: nameValue,
        description: descValue,
        flow_definition: { nodes, edges },
      });
      loadChatflows();
    } catch (err: any) {
      setError(err.message || 'Failed to save chatflow');
    } finally {
      setSaving(false);
    }
  };

  const deleteChatflow = async (id: string) => {
    if (!confirm('Are you sure you want to delete this chatflow?')) return;
    try {
      await chatflowsApi.deleteChatflow(id);
      loadChatflows();
      if (currentChatflow?.id === id) {
        setCurrentChatflow(null);
        setView('list');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete chatflow');
    }
  };

  const duplicateChatflow = async (id: string) => {
    try {
      await chatflowsApi.duplicateChatflow(id);
      loadChatflows();
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate chatflow');
    }
  };

  const toggleActive = async (chatflow: Chatflow) => {
    try {
      if (chatflow.is_active) {
        await chatflowsApi.deactivateChatflow(chatflow.id);
      } else {
        await chatflowsApi.activateChatflow(chatflow.id);
      }
      loadChatflows();
      if (currentChatflow?.id === chatflow.id) {
        setCurrentChatflow({ ...chatflow, is_active: !chatflow.is_active });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to toggle chatflow status');
    }
  };

  // ---------- Node Operations ----------
  const addNode = (type: NodeType, afterIndex?: number) => {
    const newNode: ChatflowNode = {
      id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      data: {},
      position: { x: 0, y: (afterIndex !== undefined ? afterIndex + 1 : nodes.length) * 120 },
    };
    if (afterIndex !== undefined) {
      const updated = [...nodes];
      updated.splice(afterIndex + 1, 0, newNode);
      setNodes(updated);
    } else {
      setNodes([...nodes, newNode]);
    }
    // Auto-connect edges
    rebuildEdges([...(afterIndex !== undefined ? (() => { const u = [...nodes]; u.splice(afterIndex + 1, 0, newNode); return u; })() : [...nodes, newNode])]);
    setSelectedNodeId(newNode.id);
  };

  const deleteNode = (nodeId: string) => {
    const updated = nodes.filter(n => n.id !== nodeId);
    setNodes(updated);
    rebuildEdges(updated);
    if (selectedNodeId === nodeId) setSelectedNodeId(null);
  };

  const updateNodeData = (nodeId: string, data: Record<string, any>) => {
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, data } : n));
  };

  const rebuildEdges = (nodeList: ChatflowNode[]) => {
    const newEdges: ChatflowEdge[] = [];
    for (let i = 0; i < nodeList.length - 1; i++) {
      newEdges.push({ source: nodeList[i].id, target: nodeList[i + 1].id });
    }
    setEdges(newEdges);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = nodes.findIndex(n => n.id === active.id);
    const newIndex = nodes.findIndex(n => n.id === over.id);
    const reordered = arrayMove(nodes, oldIndex, newIndex);
    setNodes(reordered);
    rebuildEdges(reordered);
  };

  // ---------- Session Operations ----------
  const handleHumanTakeover = async (sessionId: string) => {
    try {
      await chatflowsApi.humanTakeover(sessionId);
      if (currentChatflow) await loadSessions(currentChatflow.id);
    } catch (err: any) {
      setError(err.message || 'Failed to take over session');
    }
  };

  const handleReleaseSession = async (sessionId: string) => {
    try {
      await chatflowsApi.releaseSession(sessionId);
      if (currentChatflow) await loadSessions(currentChatflow.id);
    } catch (err: any) {
      setError(err.message || 'Failed to release session');
    }
  };

  const handleSendMessage = async (sessionId: string) => {
    if (!humanMessage.trim()) return;
    try {
      await chatflowsApi.sendSessionMessage(sessionId, humanMessage);
      setHumanMessage('');
      // Reload session to get updated history
      const session = await chatflowsApi.getSession(sessionId);
      setSessionMessages(prev => ({ ...prev, [sessionId]: session.conversation_history || [] }));
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    }
  };

  const viewSessionHistory = async (sessionId: string) => {
    setSelectedSessionId(sessionId);
    try {
      const session = await chatflowsApi.getSession(sessionId);
      setSessionMessages(prev => ({ ...prev, [sessionId]: session.conversation_history || [] }));
    } catch (err: any) {
      setError(err.message || 'Failed to load session history');
    }
  };

  // ---------- Derived ----------
  const selectedNode = useMemo(
    () => nodes.find(n => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  const [addNodeMenuIndex, setAddNodeMenuIndex] = useState<number | null>(null);

  // ============================================================================
  // RENDER: LIST VIEW
  // ============================================================================

  if (view === 'list') {
    return (
      <div className="p-6 w-full">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-end gap-2 mb-6">
          <button
            onClick={() => createChatflow()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create New
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
              <X className="w-4 h-4 inline" />
            </button>
          </div>
        )}

        {/* Template Gallery */}
        {templates.length > 0 && (
          <div className="mb-6">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-600 mb-3 hover:text-gray-800"
            >
              {showTemplates ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Template Gallery
            </button>
            {showTemplates && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {templates.map(template => (
                  <div
                    key={template.id}
                    className="border rounded-lg p-4 bg-gradient-to-br from-blue-50 to-indigo-50 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => createChatflow(template)}
                  >
                    <h3 className="font-semibold text-gray-800 mb-1">{template.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {template.node_types.map(nt => {
                        const cfg = getNodeConfig(nt as NodeType);
                        return (
                          <span key={nt} className={`text-xs px-2 py-0.5 rounded-full ${cfg.bgColor} ${cfg.color}`}>
                            {cfg.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Chatflow Table */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading chatflows...</div>
        ) : chatflows.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <GitBranch className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-medium">No chatflows yet</p>
            <p className="text-sm">Create your first chatflow or use a template to get started.</p>
          </div>
        ) : (
          <div className="bg-white border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Nodes</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Sessions</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {chatflows.map(chatflow => (
                  <tr key={chatflow.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openEditor(chatflow)}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{chatflow.name}</div>
                      {chatflow.description && <div className="text-sm text-gray-500 truncate max-w-xs">{chatflow.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {chatflow.node_count || 0} node{(chatflow.node_count || 0) !== 1 ? 's' : ''}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${chatflow.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {chatflow.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{(chatflow as any).session_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{chatflow.created_at ? new Date(chatflow.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={(e) => { e.stopPropagation(); openEditor(chatflow); }} className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50">Edit</button>
                        <button onClick={(e) => { e.stopPropagation(); duplicateChatflow(chatflow.id); }} className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-100">Copy</button>
                        <button onClick={(e) => { e.stopPropagation(); toggleActive(chatflow); }} className={`text-xs px-2 py-1 rounded ${chatflow.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}>
                          {chatflow.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); openSessions(chatflow); }} className="text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded hover:bg-indigo-50">Sessions</button>
                        <button onClick={(e) => { e.stopPropagation(); deleteChatflow(chatflow.id); }} className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ============================================================================
  // RENDER: EDITOR VIEW
  // ============================================================================

  if (view === 'editor' && currentChatflow) {
    return (
      <div className="h-full flex flex-col">
        {/* Top Bar */}
        <div className="border-b bg-white px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => { setView('list'); setCurrentChatflow(null); }}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            &larr; Back
          </button>
          <div className="flex-1">
            {editingName ? (
              <div className="flex items-center gap-2">
                <input
                  className="border rounded px-2 py-1 text-lg font-semibold focus:ring-2 focus:ring-blue-400"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onBlur={() => setEditingName(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setEditingName(false)}
                  autoFocus
                />
                <input
                  className="border rounded px-2 py-1 text-sm text-gray-500"
                  value={descValue}
                  onChange={(e) => setDescValue(e.target.value)}
                  placeholder="Description..."
                />
              </div>
            ) : (
              <h2
                className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-blue-600"
                onClick={() => setEditingName(true)}
              >
                {nameValue}
                {descValue && <span className="text-sm font-normal text-gray-400 ml-2">- {descValue}</span>}
              </h2>
            )}
          </div>
          <button
            onClick={() => toggleActive(currentChatflow)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              currentChatflow.is_active
                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {currentChatflow.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={saveChatflow}
            disabled={saving}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium transition-colors"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-2 text-sm">
            {error}
            <button onClick={() => setError(null)} className="ml-2"><X className="w-3 h-3 inline" /></button>
          </div>
        )}

        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar: Node Palette */}
          <div className="w-56 border-r bg-gray-50 overflow-y-auto p-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Add Node</h3>
            <div className="space-y-2">
              {NODE_TYPE_CONFIGS.map(config => {
                const Icon = config.icon;
                return (
                  <button
                    key={config.type}
                    onClick={() => addNode(config.type)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${config.bgColor} ${config.color} hover:opacity-80 border ${config.borderColor}`}
                  >
                    <Icon className="w-4 h-4" />
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Canvas */}
          <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
            {nodes.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <MessageSquare className="w-16 h-16 mb-4" />
                <p className="text-lg font-medium mb-1">Empty Flow</p>
                <p className="text-sm">Click a node type on the left to add your first step.</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={nodes.map(n => n.id)} strategy={verticalListSortingStrategy}>
                  <div className="max-w-lg mx-auto space-y-1">
                    {nodes.map((node, index) => {
                      const config = getNodeConfig(node.type);
                      return (
                        <React.Fragment key={node.id}>
                          <SortableNode
                            node={node}
                            isSelected={selectedNodeId === node.id}
                            onSelect={() => setSelectedNodeId(node.id)}
                            onDelete={() => deleteNode(node.id)}
                            config={config}
                          />
                          {/* Arrow + Add button between nodes */}
                          <div className="flex flex-col items-center py-1 relative">
                            <div className="w-0.5 h-4 bg-gray-300" />
                            <ArrowRight className="w-4 h-4 text-gray-300 rotate-90" />
                            <div className="relative">
                              <button
                                onClick={() => setAddNodeMenuIndex(addNodeMenuIndex === index ? null : index)}
                                className="w-6 h-6 rounded-full bg-white border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                              {addNodeMenuIndex === index && (
                                <div className="absolute z-10 left-8 top-0 bg-white border rounded-lg shadow-lg p-2 w-48">
                                  {NODE_TYPE_CONFIGS.map(cfg => {
                                    const I = cfg.icon;
                                    return (
                                      <button
                                        key={cfg.type}
                                        onClick={() => { addNode(cfg.type, index); setAddNodeMenuIndex(null); }}
                                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm ${cfg.color} hover:bg-gray-50`}
                                      >
                                        <I className="w-4 h-4" />
                                        {cfg.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            <div className="w-0.5 h-4 bg-gray-300" />
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* Right Sidebar: Node Config */}
          {selectedNode && (
            <NodeConfigPanel
              node={selectedNode}
              onUpdate={updateNodeData}
              onClose={() => setSelectedNodeId(null)}
            />
          )}
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: SESSIONS VIEW
  // ============================================================================

  if (view === 'sessions' && currentChatflow) {
    const selectedSession = sessions.find(s => s.id === selectedSessionId);
    const selectedHistory = selectedSessionId ? (sessionMessages[selectedSessionId] || []) : [];

    return (
      <div className="h-full flex flex-col">
        {/* Top Bar */}
        <div className="border-b bg-white px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => { setView('list'); setCurrentChatflow(null); }}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            &larr; Back
          </button>
          <h2 className="text-lg font-semibold text-gray-900">
            Sessions: {currentChatflow.name}
          </h2>
          <button
            onClick={() => loadSessions(currentChatflow.id)}
            className="ml-auto text-sm text-blue-600 hover:text-blue-800"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-2 text-sm">
            {error}
            <button onClick={() => setError(null)} className="ml-2"><X className="w-3 h-3 inline" /></button>
          </div>
        )}

        <div className="flex-1 flex overflow-hidden">
          {/* Session List */}
          <div className="w-96 border-r overflow-y-auto bg-white">
            {sessions.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-sm">No active sessions</p>
              </div>
            ) : (
              <div className="divide-y">
                {sessions.map(session => (
                  <div
                    key={session.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedSessionId === session.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                    onClick={() => viewSessionHistory(session.id)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm text-gray-800">
                        Member: {session.member_id}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          session.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : session.status === 'completed'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {session.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 space-y-0.5">
                      <div>Channel: {session.channel}</div>
                      {session.current_node_id && <div>Current Node: {session.current_node_id}</div>}
                      {session.last_interaction_at && (
                        <div>Last interaction: {new Date(session.last_interaction_at).toLocaleString()}</div>
                      )}
                      {session.human_takeover && (
                        <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                          Human Takeover
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 mt-2">
                      {!session.human_takeover ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleHumanTakeover(session.id); }}
                          className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded hover:bg-orange-200 transition-colors"
                        >
                          <UserCheck className="w-3 h-3 inline mr-1" />
                          Take Over
                        </button>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleReleaseSession(session.id); }}
                          className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors"
                        >
                          <Bot className="w-3 h-3 inline mr-1" />
                          Release
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Session Detail / Conversation */}
          <div className="flex-1 flex flex-col bg-gray-50">
            {selectedSession ? (
              <>
                <div className="p-4 bg-white border-b">
                  <h3 className="font-semibold text-gray-800">
                    Conversation with Member {selectedSession.member_id}
                  </h3>
                  <div className="text-xs text-gray-500">
                    Channel: {selectedSession.channel} | Status: {selectedSession.status}
                    {selectedSession.human_takeover && ' | Human Takeover Active'}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {selectedHistory.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      <p className="text-sm">No conversation history available.</p>
                    </div>
                  ) : (
                    selectedHistory.map((msg: any, idx: number) => (
                      <div
                        key={idx}
                        className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                            msg.direction === 'outbound'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border text-gray-800'
                          }`}
                        >
                          {msg.ai_generated && (
                            <div className="text-xs opacity-70 mb-0.5 flex items-center gap-1">
                              <Bot className="w-3 h-3" /> AI Generated
                            </div>
                          )}
                          <p>{msg.content}</p>
                          <div className={`text-xs mt-1 ${msg.direction === 'outbound' ? 'text-blue-200' : 'text-gray-400'}`}>
                            {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Send Message (only if human takeover) */}
                {selectedSession.human_takeover && (
                  <div className="p-4 bg-white border-t">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                        placeholder="Type a message..."
                        value={humanMessage}
                        onChange={(e) => setHumanMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(selectedSession.id)}
                      />
                      <button
                        onClick={() => handleSendMessage(selectedSession.id)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm transition-colors"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Eye className="w-12 h-12 mx-auto mb-3" />
                  <p className="text-sm">Select a session to view conversation history</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return null;
};

export default ChatflowBuilder;
